import { v4 as uuid } from 'uuid';
import { ICustomSurveyMatrixRow } from '../interfaces/ICustomSurveyMatrixRow';
import { ICustomSurveyQuestionOption } from '../interfaces/ICustomSurveyQuestionOption';

export const MATRIX_MIN_ROWS = 1;
export const MATRIX_MIN_COLUMNS = 2;
export const MATRIX_DEFAULT_QUESTION = 'Wie zufrieden sind Sie mit den folgenden Punkten?';
const MATRIX_DEFAULT_COLUMN_LABELS = [
  '1 – Sehr zufrieden',
  '2 – Relativ zufrieden',
  '3 – Weder zufrieden noch unzufrieden',
  '4 – Relativ unzufrieden',
  '5 – Sehr unzufrieden',
  'k. A.',
];

// The labels a matrix reads; satisfied by the editor's ICustomSurveyQuestion and the participant's question.
export interface IMatrixLabelSource {
  options?: ICustomSurveyQuestionOption[];
  rows?: ICustomSurveyMatrixRow[];
  questionI18n?: { options?: Record<string, string[]>; rows?: Record<string, string[]> };
}

export interface IMatrixAnswer {
  questionId: string;
  question: string; // always '' — mirrors setAnswer's payload shape
  answer: number; // 1-based column position (D-102), not a score; 0 = cleared
  feedback: string; // canonical column label
  language: string;
  matrixRowId: string;
}

export const createMatrixRow = (label: string): ICustomSurveyMatrixRow => ({ id: uuid(), label });
export const createDefaultMatrixRows = (): ICustomSurveyMatrixRow[] =>
  [1, 2, 3].map((n) => createMatrixRow(`Aussage ${n}`));
export const createDefaultMatrixColumns = (): ICustomSurveyQuestionOption[] =>
  MATRIX_DEFAULT_COLUMN_LABELS.map((label) => ({ label }));

export const addMatrixRow = (rows: ICustomSurveyMatrixRow[]): ICustomSurveyMatrixRow[] => [
  ...rows,
  createMatrixRow(`Aussage ${rows.length + 1}`),
];
export const removeMatrixRow = (rows: ICustomSurveyMatrixRow[], index: number) =>
  rows.length <= MATRIX_MIN_ROWS ? rows : rows.filter((_, i) => i !== index);
export const canRemoveMatrixRow = (rows: ICustomSurveyMatrixRow[]) => rows.length > MATRIX_MIN_ROWS;
export const canRemoveMatrixColumn = (options: ICustomSurveyQuestionOption[]) =>
  options.length > MATRIX_MIN_COLUMNS;

export const matrixAnswerKey = (questionId: string, rowId: string) => `${questionId}|${rowId}`;

export const buildMatrixAnswer = (
  questionId: string,
  rowId: string,
  options: ICustomSurveyQuestionOption[],
  columnIndex: number,
  language: string
): IMatrixAnswer => ({
  questionId,
  question: '',
  answer: columnIndex + 1,
  feedback: options[columnIndex].label,
  language,
  matrixRowId: rowId,
});

// Clicking a selected column again in an optional matrix clears the statement. It stays in the payload so
// the backend still deletes the question's earlier answer rows; column position 0 tells it not to store it.
export const buildClearedMatrixAnswer = (
  questionId: string,
  rowId: string,
  language: string
): IMatrixAnswer => ({
  questionId,
  question: '',
  answer: 0,
  feedback: '',
  language,
  matrixRowId: rowId,
});

const isAnswered = (entry: unknown) =>
  entry !== undefined && (entry as { answer?: number }).answer !== 0;

// rowId -> 0-based column index, for every row that has an answer in the participant's answer state.
export const getMatrixSelections = (
  answers: Record<string, { answer: number } | undefined>,
  questionId: string,
  rows: ICustomSurveyMatrixRow[]
): Record<string, number> => {
  const selections: Record<string, number> = {};
  for (const row of rows) {
    const answer = answers[matrixAnswerKey(questionId, row.id)];
    if (answer && isAnswered(answer)) selections[row.id] = answer.answer - 1;
  }
  return selections;
};

export const isMatrixComplete = (
  answers: Record<string, unknown>,
  questionId: string,
  rows: ICustomSurveyMatrixRow[]
) => rows.every((row) => isAnswered(answers[matrixAnswerKey(questionId, row.id)]));

// `||`, not `??`: the localisation view seeds untranslated entries with '' and they must fall back.
export const getMatrixRowLabel = (q: IMatrixLabelSource, index: number, language: string) =>
  q.questionI18n?.rows?.[language]?.[index] || q.rows?.[index]?.label || '';
export const getMatrixColumnLabel = (q: IMatrixLabelSource, index: number, language: string) =>
  q.questionI18n?.options?.[language]?.[index] || q.options?.[index]?.label || '';

export const getMatrixExportHeader = (
  position: number,
  questionText: string,
  rowIndex: number,
  rowLabel: string
) => `Frage ${position}.${rowIndex + 1} \n (${questionText} – ${rowLabel})`;

export const findMatrixAnswer = <T extends { questionId: string; matrixRowId?: string }>(
  answers: T[],
  questionId: string,
  rowId: string
) => answers.find((a) => a.questionId === questionId && a.matrixRowId === rowId);
